// src/auth/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';

export const AuthContext = createContext(null);
export const VerifyEmailContext = createContext(null);

export const AuthProvider = ({ children }) => {
    // Initialize currentUser state from localStorage for persistence across refreshes
    const [currentUser, setCurrentUser] = useState(() => {
        try {
            const storedUser = localStorage.getItem('currentUser');
            return storedUser ? JSON.parse(storedUser) : null;
        } catch (error) {
            console.error("AuthContext: Failed to parse currentUser from localStorage", error);
            return null;
        }
    });

    // Persist currentUser to localStorage whenever it changes
    useEffect(() => {
        try {
            if (currentUser) {
                localStorage.setItem('currentUser', JSON.stringify(currentUser));
            } else {
                localStorage.removeItem('currentUser');
            }
        } catch (error) {
            console.error("AuthContext: Failed to save currentUser to localStorage", error);
        }
    }, [currentUser]);

    /**
     * Logs in a user by setting their data in the current user state.
     * @param {object} userData - The user's data to store.
     */
    const login = (userData) => {
        setCurrentUser(userData);
    };

    /**
     * Logs out the current user by clearing their data.
     */
    const logout = () => {
        setCurrentUser(null);
    };

    /**
     * Updates specific fields of the current user's data.
     * This is useful for adding dynamically obtained information like otp_ref.
     * @param {object} updates - An object containing key-value pairs to update in currentUser.
     */
    const updateCurrentUser = (updates) => {
        setCurrentUser(prevUser => {
            const updatedUser = {
                ...prevUser,
                ...updates
            };
            return updatedUser;
        });
    };

    return (
        <AuthContext.Provider value={{ currentUser, login, logout, updateCurrentUser }}>
            {children}
        </AuthContext.Provider>
    );
};



export const VerifyEmailProvider = ({ children }) => {
    const [currentUserVerifyEmail, setCurrentUserVerifyEmail] = useState(() => {
        try {
            const storedUser = localStorage.getItem('currentUserVerifyEmail');
            return storedUser ? JSON.parse(storedUser) : null;
        } catch (error) {
            console.error("VerifyEmailProvider: Failed to parse currentUserVerifyEmail from localStorage", error);
            return null;
        }
    });

    // Persist currentUserVerifyEmail to localStorage whenever it changes
    useEffect(() => {
        try {
            if (currentUserVerifyEmail) {
                localStorage.setItem('currentUserVerifyEmail', JSON.stringify(currentUserVerifyEmail));
            } else {
                localStorage.removeItem('currentUserVerifyEmail');
            }
        } catch (error) {
            console.error("VerifyEmailProvider: Failed to save currentUserVerifyEmail to localStorage", error);
        }
    }, [currentUserVerifyEmail]);

    /**
     * Logs in a user by setting their data in the current user state.
     * @param {object} userData - The user's data to store.
     */
    const login = (userData) => {
        setCurrentUserVerifyEmail(userData);
    };

    /**
     * Logs out the current user by clearing their data.
     */
    const logout = () => {
        setCurrentUserVerifyEmail(null);
    };

    /**
     * Updates specific fields of the current user's data.
     * This is useful for adding dynamically obtained information like otp_ref.
     * @param {object} updates - An object containing key-value pairs to update in currentUser.
     */
    const updateCurrentUser = (updates) => {
        setCurrentUserVerifyEmail(prevUser => {
            const updatedUser = {
                ...prevUser,
                ...updates
            };
            return updatedUser;
        });
    };

    return (
        <VerifyEmailContext.Provider value={{ currentUserVerifyEmail, login, logout, updateCurrentUser }}>
            {children}
        </VerifyEmailContext.Provider>
    );
};
